import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Column,
  Grid,
  InlineLoading,
  InlineNotification,
  Loading,
  Tile,
} from "@carbon/react";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import AnalyticsKpiRow from "../../components/KPIs/AnalyticsKpiRow";
import SpendOverTimeChart from "../../components/KPIs/charts/SpendOverTimeChart";
import OffersBarChart from "../../components/KPIs/charts/OffersBarChart";
import DimensionBarChart from "../../components/KPIs/charts/DimensionBarChart";
import SourcingFunnel from "../../components/KPIs/charts/SourcingFunnel";
import PriceIndexChart from "../../components/KPIs/charts/PriceIndexChart";
import SupplierScorecard from "../../components/KPIs/SupplierScorecard/SupplierScorecard";
import RecentAwardsTable from "../../components/KPIs/RecentAwardsTable/RecentAwardsTable";
import { useAnalyticsDashboard } from "../../hooks/useAnalyticsDashboard";
import { AnalyticsDashboardResponse, AnalyticsFilters } from "../../types";
import AnalyticsFilterBar, {
  getDefaultAnalyticsFilters,
} from "./AnalyticsFilterBar";
import "./procurementAnalytics.scss";

/** The currency with the most spend — the dashboard's default lens. */
export const pickDefaultCurrency = (
  kpis: AnalyticsDashboardResponse["kpisByCurrency"]
): string | undefined =>
  [...kpis].sort((a, b) => b.spend - a.spend)[0]?.currency;

const hasNoActivity = (data: AnalyticsDashboardResponse) =>
  data.kpisByCurrency.length === 0 &&
  data.timeSeries.length === 0 &&
  data.recentAwards.length === 0 &&
  !data.funnel?.requests;

/**
 * Buyer procurement-analytics dashboard (gated on VIEW_ANALYTICS at the route): filter bar →
 * one POST /analytics/dashboard → KPI row, charts, supplier scorecard and recent awards. The
 * active currency is lifted here so a single switch drives every currency-scoped widget.
 */
const ProcurementAnalytics = () => {
  const { t } = useTranslation();
  // The query is disabled without a date range, so the filter state MUST start with one.
  const [filters, setFilters] = useState<AnalyticsFilters>(() =>
    getDefaultAnalyticsFilters()
  );
  const [pickedCurrency, setPickedCurrency] = useState<string | null>(null);

  const { data, isLoading, isFetching, isError } =
    useAnalyticsDashboard(filters);

  const currencies = useMemo(
    () => data?.kpisByCurrency.map((k) => k.currency) ?? [],
    [data]
  );
  // A pick that vanished from the latest response (e.g. after a filter change) falls back to the
  // top-spend currency rather than leaving every chart empty.
  const currency =
    pickedCurrency && currencies.includes(pickedCurrency)
      ? pickedCurrency
      : data
        ? pickDefaultCurrency(data.kpisByCurrency)
        : undefined;

  return (
    <div className="procurement-analytics">
      <PageHeader title={t("analytics.title")} />

      <AnalyticsFilterBar value={filters} onChange={setFilters} />

      {isError ? (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t("analytics.errorTitle")}
          subtitle={t("analytics.errorMessage")}
        />
      ) : null}

      {!data && isLoading ? (
        <div className="procurement-analytics__loading" role="status">
          <Loading small withOverlay={false} description={t("cta.loading")} />
          <span>{t("analytics.dashboard.loading")}</span>
        </div>
      ) : null}

      {data && isFetching ? (
        <InlineLoading
          description={t("analytics.dashboard.refreshing")}
          status="active"
        />
      ) : null}

      {data && hasNoActivity(data) ? (
        <Tile className="procurement-analytics__empty">
          <h3>{t("analytics.dashboard.emptyTitle")}</h3>
          <p>{t("analytics.dashboard.emptyBody")}</p>
        </Tile>
      ) : null}

      {data && !hasNoActivity(data) ? (
        <>
          <AnalyticsKpiRow
            kpisByCurrency={data.kpisByCurrency}
            currency={currency}
            onCurrencyChange={setPickedCurrency}
          />

          <Grid fullWidth narrow className="procurement-analytics__grid">
            {/* Currency-scoped widgets: only meaningful once the window has an award currency.
                Layout echoes the design mockup's rhythm — a wide primary chart paired with a
                narrow companion on each of the first two rows, then balanced halves. */}
            {currency ? (
              <>
                {/* Spend trend + the sourcing funnel beside it. */}
                <Column sm={4} md={8} lg={10}>
                  <SpendOverTimeChart
                    timeSeries={data.timeSeries}
                    currency={currency}
                  />
                </Column>
                <Column sm={4} md={8} lg={6}>
                  <SourcingFunnel funnel={data.funnel} />
                </Column>

                {/* Offer depth + who is sourcing. */}
                <Column sm={4} md={8} lg={10}>
                  <OffersBarChart
                    timeSeries={data.timeSeries}
                    currency={currency}
                  />
                </Column>
                <Column sm={4} md={4} lg={6}>
                  <DimensionBarChart
                    title={t("analytics.dashboard.spendByBuyer")}
                    data={(data.byDimension?.buyer ?? []).filter(
                      (slice) => slice.currency === currency
                    )}
                  />
                </Column>

                {/* Spend breakdowns. */}
                <Column sm={4} md={4} lg={8}>
                  <DimensionBarChart
                    title={t("analytics.dashboard.spendByFamily")}
                    data={(data.byDimension?.family ?? []).filter(
                      (slice) => slice.currency === currency
                    )}
                  />
                </Column>
                <Column sm={4} md={4} lg={8}>
                  <DimensionBarChart
                    title={t("analytics.dashboard.spendBySupplier")}
                    data={(data.byDimension?.supplier ?? []).filter(
                      (slice) => slice.currency === currency
                    )}
                  />
                </Column>

                {/* Tag mix + price trend. */}
                <Column sm={4} md={4} lg={8}>
                  <DimensionBarChart
                    title={t("analytics.dashboard.spendByTag")}
                    data={(data.byDimension?.tag ?? []).filter(
                      (slice) => slice.currency === currency
                    )}
                  />
                </Column>
                <Column sm={4} md={8} lg={8}>
                  <PriceIndexChart
                    priceSeries={data.priceSeries}
                    currency={currency}
                  />
                </Column>

                <Column sm={4} md={8} lg={16}>
                  <SupplierScorecard
                    suppliers={data.suppliers}
                    currency={currency}
                  />
                </Column>
              </>
            ) : (
              /* Count-only: shown even with requests but no awards (hence no currency). */
              <Column sm={4} md={8} lg={16}>
                <SourcingFunnel funnel={data.funnel} />
              </Column>
            )}

            {/* Rows carry their own currency, so no active-currency scoping needed. */}
            <Column sm={4} md={8} lg={16}>
              <RecentAwardsTable awards={data.recentAwards} />
            </Column>
          </Grid>
        </>
      ) : null}
    </div>
  );
};

export default ProcurementAnalytics;
